import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { AnalyticsEventSource, ScreenEventAnalytics } from '@atlassian/temp-analytics';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { AppContent, AppPage, useFeatureToggle } from '@css-xp/base';
import {
  routePaths,
  STATSIG_FEATURE_GATES,
  ENABLE_DESIGN_ITERATION_1,
  CATALOGUE_CONSTANTS,
} from '@css-xp/ppc-constants';
import { PageBackgroundV2 } from '@css-xp/page-background';
import { fetchNativeCatalogProductDetails } from '@css-xp/ppc-api';
import { CatalogDetailsPageLoader } from '@css-xp/ppc-page-loaders';
import { ProductPricingPane } from './sidepane/product-pricing-pane';
import { IncludedProductsSidebar } from './sidepane/included-products-sidebar';

const allowedSidebars = {
  [CATALOGUE_CONSTANTS.Sidebar.INCLUDED_PRODUCTS]: IncludedProductsSidebar,
};

export const CatalogDetailsPage = () => {
  const { productId } = useParams<{ txaId: string; productId: string }>();
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isJSMRepackagingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JSM_OFFERING_CHANGE);
  const isCompassPremiumEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_COMPASS_PREMIUM);
  const isJPDAsJiraFamilyEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY);
  const isAddOnsSearchEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2);
  const isStatuspageEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_STATUSPAGE);
  const isTeamWorkCollectionEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1);
  const isStrategyCollectionEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_STRATEGY_COLLECTION);
  const isTWCPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_2);
  const isAppEditionSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_APP_EDITION_SUPPORT);
  const isMultiInstanceAppEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_INSTANCE_APPS);
  const isBitbucketProductEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_BITBUCKET);

  const sxpClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.SXP), []);

  const { data, loading } = useQuery(fetchNativeCatalogProductDetails, {
    variables: {
      id: productId,
      filterOptions: [
        {
          key: 'includeProperties',
          values: ['vendorName'],
        },
      ],
      isAddOnsSearchEnabled,
      isStatuspageEnabled,
      isTeamWorkCollectionEnabled,
      isStrategyCollectionEnabled,
      isBitbucketProductEnabled,
    },
    fetchPolicy: 'no-cache',
    client: sxpClient,
  });

  if (loading) {
    return <CatalogDetailsPageLoader />;
  }

  const product = data?.nativeCatalog?.nodes[0];

  const renderAppPage = () => {
    return (
      <AppPage allowedSidebars={allowedSidebars} className="app-page-v2 invoice-details-page">
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.PARTNER_CATALOG_DETAILS_SCREEN,
            attributes: {},
          }}
        />
        <AppContent>
          <ProductPricingPane
            product={product}
            hasOptedInForPartnerPromotion={product?.hasOptedInForPartnerPromotion}
            isEnterpriseOfferingEnabled={true}
            isJSMRepackagingEnabled={isJSMRepackagingEnabled}
            isCompassPremiumEnabled={isCompassPremiumEnabled}
            isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
            version={isDesignIteration1Enabled ? 2 : null}
            showDefaultAnnualPricing={true}
            isTWCPhase2Enabled={isTWCPhase2Enabled}
            isAppEditionSupportEnabled={isAppEditionSupportEnabled}
            isMultiInstanceAppEnabled={isMultiInstanceAppEnabled}
          />
        </AppContent>
      </AppPage>
    );
  };

  const breadcrumbsItems = [
    {
      title: 'Home',
      href: routePaths.home,
    },
    {
      title: 'Catalog',
      href: routePaths.catalog,
    },
    ...(product
      ? [
          {
            title: product.name,
            href: routePaths.catalogDetails,
          },
        ]
      : []),
  ];

  return (
    <PageBackgroundV2 heading={''} breadcrumbsProps={{ items: breadcrumbsItems }}>
      {renderAppPage()}
    </PageBackgroundV2>
  );
};
